/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import helpcenter from 'js/lib/courseraHelpcenter';
import { isAuthenticatedUser } from 'js/lib/user';

import { IconButton, MenuList, Popover } from '@coursera/cds-core';
import { HelpIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { getAddBoostChatScriptToPage, getChatPanel } from 'bundles/common/components/BoostChat/utils/BoostUtils';
import { getActiveConversationId, getChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';
import useBoostChatConfig from 'bundles/composable-page-header/hooks/useBoostChatConfig';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  iconColor: css`
    color: var(--cds-color-interactive-secondary-inactive);
  `,
  linkItem: css`
    &:focus,
    &:hover {
      text-decoration: none;
    }
  `,
};

const useShouldShowChatOption = (renderBoostChatOnSsr: boolean) => {
  const [isChatAvailable, setIsChatAvailable] = React.useState(false);
  const { enableChat, chatConfig } = useBoostChatConfig({ renderBoostChatOnSsr });
  const shouldShowHeaderHelpDropdown = useShouldShowHelpDropdown();

  React.useEffect(() => {
    const hasConversation = !!getActiveConversationId();
    const hasTargetingRule = !!getChatTargetingRule(
      location.href,
      enableChat,
      chatConfig,
      shouldShowHeaderHelpDropdown
    );
    setIsChatAvailable(hasConversation || hasTargetingRule);
  }, [enableChat, chatConfig, shouldShowHeaderHelpDropdown]);

  return isChatAvailable;
};

const HelpDropdownGridItem = ({ disableChatSsr }: { disableChatSsr?: boolean }) => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const trackV3Event = useTracker();
  const showChatOption = useShouldShowChatOption(!disableChatSsr);

  const handleOpenChat = () => {
    setPopoverAnchor(null);

    const chatPanel = getChatPanel();
    if (chatPanel) {
      chatPanel.show();
    } else {
      const addScript = getAddBoostChatScriptToPage(() => {
        getChatPanel()?.show();
      });
      addScript();
    }

    trackV3Event('click_button', {
      button: { name: 'boost_chat_open' },
    });
  };

  const helpCenterUrl = helpcenter.getNewHelpCenterHome(isAuthenticatedUser());

  const handleOpenHelpCenter = () => {
    setPopoverAnchor(null);

    trackV3Event('click_button', {
      button: { name: 'help_menu_help_center' },
    });
  };

  React.useEffect(() => {
    if (popoverAnchor) {
      trackV3Event('open_navigation_menu', {
        navigationType: 'help',
      });
    }
  }, [popoverAnchor, trackV3Event]);

  return (
    <div>
      <IconButtonWrapper
        iconButton={
          <IconButton
            variant="ghost"
            icon={<HelpIcon css={styles.iconColor} />}
            onClick={(e: { currentTarget: React.SetStateAction<HTMLButtonElement | null> }) =>
              setPopoverAnchor(e.currentTarget)
            }
            aria-expanded={Boolean(popoverAnchor)}
            aria-label={_t('Help menu')}
            tooltip={_t('Help')}
            size="small"
            data-testid="header-help-button"
          />
        }
        buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
        svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
      />
      <Popover open={Boolean(popoverAnchor)} anchorElement={popoverAnchor} onClose={() => setPopoverAnchor(null)}>
        <Popover.Body component="div" tabIndex={0}>
          <MenuList>
            <MenuList.ItemLink
              role="menuitem"
              href={helpCenterUrl}
              target="_blank"
              rel="noopener noreferrer"
              css={styles.linkItem}
              onClick={handleOpenHelpCenter}
              data-testid="help-dropdown-help-center"
            >
              {_t('Help Center')}
            </MenuList.ItemLink>
            {showChatOption && (
              <MenuList.Item role="menuitem" onClick={handleOpenChat} data-testid="help-dropdown-chat-with-us">
                {_t('Chat with us')}
              </MenuList.Item>
            )}
          </MenuList>
        </Popover.Body>
      </Popover>
    </div>
  );
};

export default HelpDropdownGridItem;
